import React from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';

const features = [
  { label: 'Instant inbox, zero friction', detail: 'Live address in under 3 seconds — no verification needed' },
  { label: 'Custom domains supported', detail: 'Apply your own domain or choose from mailnova.io, dropbox.email' },
  { label: 'Permanent addresses from $4/mo', detail: 'Upgrade to Pro and your address never expires' },
  { label: 'Up to 200 GB cloud storage', detail: 'Attachments stored securely, searchable any time' },
];

export default function AuthLeftPanel() {
  return (
    <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative flex-col overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-mesh" />
      <div className="absolute inset-0 bg-grid-pattern opacity-60" />

      {/* Floating glass orbs */}
      <div className="absolute top-20 left-16 w-48 h-48 rounded-full opacity-40 animate-float"
        style={{ background: 'radial-gradient(circle, rgba(91,79,232,0.25) 0%, transparent 70%)', filter: 'blur(20px)' }} />
      <div className="absolute bottom-32 right-12 w-64 h-64 rounded-full opacity-30 animate-float"
        style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.2) 0%, transparent 70%)', filter: 'blur(24px)', animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-1/3 w-32 h-32 rounded-full opacity-25 animate-float"
        style={{ background: 'radial-gradient(circle, rgba(165,180,252,0.3) 0%, transparent 70%)', filter: 'blur(16px)', animationDelay: '4s' }} />

      <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-3 mb-auto">
          <div className="glass-intense rounded-xl p-2">
            <AppLogo size={28} />
          </div>
          <span className="text-xl font-bold tracking-tight text-foreground">CompanyInbox</span>
        </Link>

        {/* Central content */}
        <div className="py-12">
          <div className="badge-active w-fit mb-5">Privacy-first email</div>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-4 text-foreground">
            Professional email,{' '}
            <span className="text-gradient-primary">without the noise.</span>
          </h1>
          <p className="text-secondary-foreground text-lg leading-relaxed mb-10 max-w-md">
            Instant disposable inboxes for signups, testing, and anything you don&apos;t want in your real inbox. Upgrade to keep it forever.
          </p>

          {/* Feature list */}
          <ul className="space-y-4">
            {features?.map((f) => (
              <li key={`feature-${f?.label}`} className="flex items-start gap-3">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center flex-shrink-0">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5L4 7L8 3" stroke="#5B4FE8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{f?.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{f?.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Glass card at bottom */}
        <div className="glass-card p-5 rounded-2xl">
          <p className="text-sm font-semibold text-foreground mb-1">Trusted by professionals worldwide</p>
          <p className="text-xs text-muted-foreground">
            <span className="text-foreground font-bold">84,000+</span> addresses generated this month across 120+ countries
          </p>
        </div>
      </div>
    </div>
  );
}
